/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eoc-fetch is already being used in eoc resources, so there is no reason not
// to override the whitelist here.

import getPillarLocalDevelopment from '@jira/ssr__global-build-context/src/getPillarLocalDevelopment.tsx';
// eslint-disable-next-line jira/import-whitelist
import { fetch } from '@atlassian/eoc-fetch';
import { fg } from '@atlassian/jira-feature-gating';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';
import { getReroutableURL } from '@atlassian/jira-fetch/src/utils/get-reroutable-url.tsx';
import {
	type TenantInfo,
	type TenantInfoClientResponse,
	type TenantInfoServerResponse,
	ConsolidationState,
} from './TenantInfo.types.tsx';
import { CONSOLIDATION_STATE_RESOURCE_TYPE } from './consolidationStateResourceType.constants.tsx';

/**
 * Does the request to the internal OpsGenie gateway if run on the server.
 * On the client, will do a regular request with the cloudid prefix.
 */
export const fetchConsolidationState = (
	cloudId: string,
	atlassianAccountId: string | null,
	isAnonymous: boolean,
	appPermissions?: {
		hasServiceDeskAccess: boolean;
	},
): Promise<TenantInfo> => {
	let url = fg('operations-tenant-info-endpoint-update')
		? '/internal/v1/users/ops-user-info'
		: '/internal/v1/customers/tenant-info';
	if (cloudId) {
		url = `${url}/${cloudId}`;
	} else {
		throw new Error('No tenant info exists.');
	}

	let promise: Promise<TenantInfo>;

	if ((appPermissions && appPermissions.hasServiceDeskAccess === false) || isAnonymous) {
		promise = new Promise((resolve) => {
			resolve({
				consolidationState: ConsolidationState.NOT_CONSOLIDATED,
				...(fg('operations-tenant-info-endpoint-update')
					? {
							isAdmin: false,
						}
					: {}),
			});
		});
		return promise;
	}

	if (fg('operations-tenant-info-endpoint-update')) {
		url = `${url}/${atlassianAccountId}`;
	}

	if (__SERVER__ && !getPillarLocalDevelopment()) {
		promise = fetch<TenantInfoServerResponse>(url, {
			exactUrl: true,
			headers: {
				'x-ssr-service-name': fg('operations-tenant-info-endpoint-update')
					? 'og-apps-user-int-api'
					: 'og-customer-int-api',
			},
		});
	} else {
		const clientUrl = fg('operations-tenant-info-endpoint-update')
			? '/v1/users/ops-user-info'
			: '/v1/customers/tenant-info';
		promise = fetch<TenantInfoClientResponse>(
			getReroutableURL(`/gateway/api/jsm/ops/web/${cloudId}${clientUrl}`),
			{ exactUrl: true },
		).then(({ data }) => data);
	}

	return promise;
};

const getConsolidationStateResourceFactory = (forceBrowser = false) =>
	createResource<TenantInfo>({
		type: CONSOLIDATION_STATE_RESOURCE_TYPE,
		getKey: () => 'tenantInformation',
		getData: (_, { tenantContext: { cloudId, appPermissions, atlassianAccountId, isAnonymous } }) =>
			fetchConsolidationState(cloudId, atlassianAccountId, isAnonymous, appPermissions),
		maxAge: Infinity,
		isBrowserOnly: forceBrowser,
		isCritical: true,
	});

const consolidationResourceForceBrowser = getConsolidationStateResourceFactory(true);
const consolidationResourceDontForceBrowser = getConsolidationStateResourceFactory(false);

/**
 * This is SSR-enabled and will do the request on SSR whenever possible.
 * To disable this behaviour, provide the optional parameter as true.
 * @deprecated This resource is deprecated to use as parameter to useResource, only usage should be adding to resource property of a route. Please migrate to useOgTenantInfo hook.
 */
export const getConsolidationStateResource = (forceBrowser = false): RouteResource<TenantInfo> => {
	/**
	 * This needs to return the same resource instance each time (based on args) or else we end up with many unnecessary re-renders
	 */
	return forceBrowser ? consolidationResourceForceBrowser : consolidationResourceDontForceBrowser;
};
